import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, 
  Folder, 
  MessageCircle,
  FileText,
  CheckSquare,
  Trash2
} from 'lucide-react';
import { useNoteStore } from '../stores/noteStore';
import { useNavigate } from 'react-router-dom';

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const {
    folders,
    tags,
    notes,
    selectedFolder,
    selectedTags,
    createNote,
    createFolder,
    deleteFolder,
    setSelectedFolder,
    setSelectedTags,
  } = useNoteStore();

  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  const handleCreateNote = async (type: 'text' | 'checklist' = 'text') => {
    await createNote(type);
    navigate('/');
  };

  const handleCreateFolder = async () => {
    if (newFolderName.trim()) {
      const colors = ['#FFE4E1', '#E1F5FE', '#F3E5F5', '#E8F5E8', '#FFF3E0', '#FCE7F3', '#DBEAFE'];
      const randomColor = colors[Math.floor(Math.random() * colors.length)];
      await createFolder(newFolderName.trim(), randomColor);
      setNewFolderName('');
      setShowNewFolder(false);
    }
  };

  const handleFolderSelect = (folderId: string | null) => {
    setSelectedFolder(folderId);
    navigate('/');
  };

  const handleTagToggle = (tagName: string) => {
    const newTags = selectedTags.includes(tagName)
      ? selectedTags.filter(t => t !== tagName)
      : [...selectedTags, tagName];
    setSelectedTags(newTags);
    navigate('/');
  };

  const getNotesCountInFolder = (folderId: string | null) => {
    if (folderId === null) return notes.length;
    return notes.filter(n => n.folderId === folderId).length;
  };

  return (
    <div className="flex flex-col h-full">
      {/* Logo - Desktop only */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-4 border-b border-gray-100 hidden md:block"
      >
        <h1 className="text-xl font-bold text-notepad-text flex items-center gap-2">
          <img src="/favicon.svg" alt="Membank" className="w-6 h-6" />
          <span>
            Mem<span className="font-mono text-yellow-600">bank</span>
          </span>
        </h1>
        <p className="text-xs text-gray-500 mt-1">Simple & Fast Notes</p>
      </motion.div>

      {/* Quick Actions - Desktop only */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-4 space-y-2 hidden md:block"
      >
        <button
          onClick={() => handleCreateNote('text')}
          className="w-full bg-yellow-400 hover:bg-yellow-500 text-notepad-text font-medium py-2.5 px-4 rounded-xl flex items-center gap-2 justify-center transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Note
        </button>
        
        <button
          onClick={() => handleCreateNote('checklist')}
          className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2.5 px-4 rounded-xl flex items-center gap-2 justify-center transition-colors"
        >
          <CheckSquare className="w-4 h-4" />
          New Checklist
        </button>
      </motion.div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Folders Section */}
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Folders
            </h3>
            <button
              onClick={() => setShowNewFolder(true)}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4 text-gray-500" />
            </button>
          </div>

          {showNewFolder && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mb-3"
            >
              <input
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleCreateFolder()}
                onBlur={() => {
                  if (!newFolderName.trim()) setShowNewFolder(false);
                }}
                placeholder="Folder name..."
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                autoFocus
              />
              <div className="flex gap-2 mt-2">
                <button
                  onClick={handleCreateFolder}
                  className="flex-1 py-1.5 bg-yellow-400 text-notepad-text text-sm font-medium rounded-lg"
                >
                  Create
                </button>
                <button
                  onClick={() => {
                    setShowNewFolder(false);
                    setNewFolderName('');
                  }}
                  className="flex-1 py-1.5 bg-gray-100 text-gray-600 text-sm font-medium rounded-lg"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          )}

          <div className="space-y-1">
            {/* All Notes */}
            <button
              onClick={() => handleFolderSelect(null)}
              className={`w-full text-left px-3 py-2.5 rounded-xl text-sm transition-all flex items-center gap-3 ${
                selectedFolder === null
                  ? 'bg-yellow-100 text-notepad-text font-medium'
                  : 'hover:bg-gray-50 text-gray-700'
              }`}
            >
              <div className="w-8 h-8 bg-yellow-200 rounded-lg flex items-center justify-center">
                <FileText className="w-4 h-4 text-yellow-700" />
              </div>
              <span className="flex-1">All Notes</span>
              <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                {getNotesCountInFolder(null)}
              </span>
            </button>
            
            {/* Folder List */}
            {folders.map((folder) => (
              <div key={folder.id} className="group relative">
                <button
                  onClick={() => handleFolderSelect(folder.id)}
                  className={`w-full text-left px-3 py-2.5 rounded-xl text-sm transition-all flex items-center gap-3 ${
                    selectedFolder === folder.id
                      ? 'bg-yellow-100 text-notepad-text font-medium'
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: folder.color }}
                  >
                    <Folder className="w-4 h-4 text-gray-600" />
                  </div>
                  <span className="flex-1 truncate">{folder.name}</span>
                  <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                    {getNotesCountInFolder(folder.id)}
                  </span>
                </button>
                
                {/* Delete button on hover */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (confirm(`Delete folder "${folder.name}"? Notes will be moved to All Notes.`)) {
                      deleteFolder(folder.id);
                    }
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 opacity-0 group-hover:opacity-100 hover:bg-red-100 rounded-lg transition-all"
                >
                  <Trash2 className="w-3.5 h-3.5 text-red-500" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Tags Section */}
        <div className="p-4 border-t border-gray-100">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
            Tags
          </h3>
          
          {tags.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">
              No tags yet. Add tags to your notes to organize them.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {tags.map((tag) => (
                <button
                  key={tag.id}
                  onClick={() => handleTagToggle(tag.name)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-all ${
                    selectedTags.includes(tag.name)
                      ? 'bg-yellow-400 text-notepad-text font-medium'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: tag.color }}
                  />
                  {tag.name}
                  <span className="text-xs opacity-60">{tag.count}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Bottom Actions - Desktop only */}
      <div className="p-4 border-t border-gray-100 hidden md:block">
        <button
          onClick={() => navigate('/ai-chat')}
          className="w-full bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white font-medium py-2.5 px-4 rounded-xl flex items-center gap-2 justify-center transition-all"
        >
          <MessageCircle className="w-4 h-4" />
          AI Assistant
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
